import { missions, themeFor, type Mission } from './missions.ts'
import { roleDeclarations, type ThemePair } from './theme.ts'
import type { DesignToken } from './system.ts'

// CSS rendering for the published token and theme files. The site imports the
// same files it publishes, so a download is exactly what the page shows.

const block = (selector: string, body: string) => `${selector} {\n${body}\n}\n`

/** Core tokens plus the default American roles, light and dark. */
export function tokensCss(tokens: DesignToken[], banner: string, theme: ThemePair) {
  const core = tokens
    .filter((token) => token.group !== 'color')
    .map((token) => `  ${token.css}: ${token.value};`)
    .join('\n')
  return [
    `/* ${banner} */`,
    block(':root', `  color-scheme: light;\n${core}\n${roleDeclarations(theme.light)}`),
    block('[data-theme="light"]', `  color-scheme: light;\n${roleDeclarations(theme.light)}`),
    block('[data-theme="dark"]', `  color-scheme: dark;\n${roleDeclarations(theme.dark)}`),
    `@media (prefers-color-scheme: dark) {\n${block(
      ':root:not([data-theme])',
      `  color-scheme: dark;\n${roleDeclarations(theme.dark)}`,
    ).replace(/^/gm, '  ').trimEnd()}\n}\n`,
  ].join('\n')
}

/**
 * Every mission as attribute-scoped rules. `data-mission` works on <html> to
 * reskin the site, or on any element to preview a theme in place; a
 * `data-theme` on that same element pins its mode.
 */
export function themesCss(banner: string, list: Mission[] = missions) {
  return [
    `/* ${banner} */`,
    ...list.map((mission) => {
      const theme = themeFor(mission)
      const id = `[data-mission="${mission.id}"]`
      return [
        block(id, roleDeclarations(theme.light)),
        block(
          `[data-theme="dark"] ${id},\n[data-theme="dark"]${id}`,
          `  color-scheme: dark;\n${roleDeclarations(theme.dark)}`,
        ),
        block(
          `[data-theme="light"]${id}`,
          `  color-scheme: light;\n${roleDeclarations(theme.light)}`,
        ),
      ].join('')
    }),
  ].join('\n')
}

/** A standalone theme for one mission, ready to drop into another project. */
export function missionCss(mission: Mission, banner: string) {
  const theme = themeFor(mission)
  return [
    `/* ${banner} */`,
    block(':root', `  color-scheme: light;\n${roleDeclarations(theme.light)}`),
    block('[data-theme="dark"]', `  color-scheme: dark;\n${roleDeclarations(theme.dark)}`),
    `@media (prefers-color-scheme: dark) {\n${block(
      ':root:not([data-theme="light"])',
      `  color-scheme: dark;\n${roleDeclarations(theme.dark)}`,
    ).replace(/^/gm, '  ').trimEnd()}\n}\n`,
  ].join('\n')
}
